import React, { useEffect, useState, useRef } from 'react';
import './Credit.css';

interface CreditFilterDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: {
    fromDate?: string;
    toDate?: string;
    isCreditSpent?: boolean;
    isCreditReceived?: boolean;
  }) => void;
  initialFromDate?: string;
  initialToDate?: string;
  initialStatus?: string;
}

const CreditFilterDropdown: React.FC<CreditFilterDropdownProps> = ({ 
  isOpen, 
  onClose, 
  onApplyFilters,
  initialFromDate = '',
  initialToDate = '',
  initialStatus = ''
}) => {
  const [fromDate, setFromDate] = useState<string>(initialFromDate || '');
  const [toDate, setToDate] = useState<string>(initialToDate || '');
  const [status, setStatus] = useState<string>(initialStatus || '');
  const fromDateRef = useRef<HTMLInputElement>(null);
  const toDateRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFromDate(initialFromDate || '');
    setToDate(initialToDate || '');
    setStatus(initialStatus || '');
  }, [initialFromDate, initialToDate, initialStatus]);

  const handleApply = () => {
    const filters = {
      fromDate,
      toDate,
      isCreditSpent: status === 'spent' ? true : undefined,
      isCreditReceived: status === 'received' ? true : undefined,
    };
  
    // Remove undefined values
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== undefined)
    );
  
    onApplyFilters(cleanedFilters); // Pass only defined values
    onClose();
  };
  

  const handleClearAll = () => {
    setFromDate('');
    setToDate('');
    setStatus('');
    console.log('Cleared All Filters:', {
      fromDate: '',
      toDate: '',
      status: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div
      id="dropdown-3"
      className="z-10 bg-white divide-y divide-gray-100 rounded-lg shadow !right-0 !left-auto !mt-2 !transform-none !top-full sm:w-96 w-full"
      style={{ position: 'absolute', transform: 'translate3d(-11px, 55.5px, 0px)' }}
    >
      <ul className="px-3 text-sm text-gray-700">
        <div className="flex items-center justify-between pt-3 mx-3 rounded-t border-b border-lightgray-100 pb-4">
          <h3 className="text-base font-semibold text-grease-100">Filters</h3>
          <button
            type="button"
            className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
            onClick={onClose}
          >
            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
            </svg>
            <span className="sr-only">Close dropdown</span>
          </button>
        </div>

        {/* From Date Input */}
        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-4 p-4 mx-3 mb-4">
          <p className="text-sm">From Date</p>
          <div className="flex items-center gap-4 mt-3">
            <div className="relative flex-1">
              <input
                ref={fromDateRef}
                type="date"
                className="block w-full p-3 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                max={toDate || new Date().toISOString().split("T")[0]}
              />
              <button
                className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                onClick={() => fromDateRef.current?.showPicker()}
              >
                <i className="st-calendar text-lightgray-200"></i>
              </button>
            </div>
            <button 
              className="bg-primaryblue-50 bg-opacity-25 rounded-md h-10 w-10 flex items-center justify-center"
              onClick={() => setFromDate('')}
            >
              <i className="st-delete text-lightgray-200"></i>
            </button>
          </div>
        </div>

        {/* To Date Input */}
        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-4 p-4 mx-3 mb-4">
          <p className="text-sm">To Date</p>
          <div className="flex items-center gap-4 mt-3">
            <div className="relative flex-1">
              <input
                ref={toDateRef}
                type="date"
                className="block w-full p-3 pr-10 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                min={fromDate || undefined}
                max={new Date().toISOString().split("T")[0]}
              />
              <button
                className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                onClick={() => toDateRef.current?.showPicker()}
              >
                <i className="st-calendar text-lightgray-200"></i>
              </button>
            </div>
            <button 
              className="bg-primaryblue-50 bg-opacity-25 rounded-md h-10 w-10 flex items-center justify-center"
              onClick={() => setToDate('')}
            >
              <i className="st-delete text-lightgray-200"></i>
            </button>
          </div>
        </div>

        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-4 p-4 mx-3 mb-4 border-b border-lightgray-100">
          <p className="text-sm">Credit Status</p>
          <div className="flex sm:flex-nowrap flex-wrap items-center gap-4 mt-3">
            <div className="w-full">
              <select 
                className="block w-full border-none p-3 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="" disabled selected>Select status</option>
                <option value="spent">Credit Spent</option>
                <option value="received">Credit Received</option>
              </select>
            </div>
            <button 
              className="bg-primaryblue-50 bg-opacity-25 rounded-md h-10 w-10 flex items-center justify-center"
              onClick={() => setStatus('')}
            >
              <i className="st-delete text-lightgray-200"></i>
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mx-3 mb-4">
          <button className="flex gap-2 px-3 py-2 bg-blue-500 bg-opacity-25 rounded-md" onClick={onClose} >
            <p className="font-medium text-lightgray-200 text-xs">Cancel</p>
          </button>
          <div className="flex items-center gap-4">
            <button 
              className="flex gap-2 px-3 py-2 border border-[#FFBC00] rounded-md"
              onClick={handleClearAll}
            >
              <p className="font-medium text-[#FFBC00] text-xs">Clear All</p>
            </button>
            <button 
              className="flex gap-2 px-3 py-2 bg-[#FFBC00] rounded-md"
              onClick={handleApply}
            >
              <p className="font-medium text-[#F5F9FE] text-xs">Apply</p>
            </button>
          </div>
        </div>
      </ul>
    </div>
  );
};

export default CreditFilterDropdown;